import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Paper
} from "@material-ui/core";
import SpellcheckOutlinedIcon from "@material-ui/icons/SpellcheckOutlined";
import api from "../../services/api";
import toastError from "../../errors/toastError";

/**
 * Modal de revisao de texto. Recebe o texto a revisar e a flag
 * `isSelection` (true se for trecho selecionado, false se for input
 * inteiro). Chama POST /ai-assist/review-text automaticamente quando
 * abre. Atendente revisa "antes/depois" e clica Substituir — onApply
 * recebe o texto revisado e o pai decide onde inserir (respeitando
 * selectionStart/selectionEnd se for selecao).
 */
const ReviewTextModal = ({
  open,
  onClose,
  originalText,
  isSelection,
  ticketId,
  onApply
}) => {
  const [loading, setLoading] = useState(false);
  const [revised, setRevised] = useState("");
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!open || !originalText) return;
    let active = true;
    const run = async () => {
      setLoading(true);
      setRevised("");
      setMeta(null);
      try {
        const { data } = await api.post("/ai-assist/review-text", {
          text: originalText,
          ticketId: ticketId || null,
          isSelection: !!isSelection
        });
        if (!active) return;
        setRevised(data.revised);
        setMeta({
          model: data.model,
          provider: data.provider,
          originalLength: data.originalLength,
          revisedLength: data.revisedLength
        });
      } catch (err) {
        if (active) toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [open, originalText, isSelection, ticketId]);

  const handleApply = () => {
    if (!revised) return;
    onApply(revised);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <SpellcheckOutlinedIcon
          style={{ verticalAlign: "middle", marginRight: 8, color: "#0277bd" }}
        />
        Revisar mensagem
        {isSelection && (
          <Typography
            component="span"
            variant="caption"
            color="textSecondary"
            style={{ marginLeft: 8 }}
          >
            (apenas o trecho selecionado)
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: "block", marginBottom: 4, fontWeight: 600 }}
        >
          Texto original:
        </Typography>
        <Paper
          variant="outlined"
          style={{
            padding: 8,
            background: "#fafafa",
            fontSize: 13,
            whiteSpace: "pre-wrap",
            marginBottom: 12,
            color: "#444"
          }}
        >
          {originalText}
        </Paper>

        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: "block", marginBottom: 4, fontWeight: 600 }}
        >
          Texto revisado pela IA:
        </Typography>
        {loading ? (
          <Paper
            variant="outlined"
            style={{
              padding: 24,
              textAlign: "center",
              borderColor: "#0277bd"
            }}
          >
            <CircularProgress size={20} />
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ display: "block", marginTop: 8 }}
            >
              Revisando...
            </Typography>
          </Paper>
        ) : (
          revised && (
            <Paper
              variant="outlined"
              style={{
                padding: 8,
                background: "#e3f2fd",
                fontSize: 13,
                whiteSpace: "pre-wrap",
                borderColor: "#0277bd",
                color: "#0d47a1"
              }}
            >
              {revised}
            </Paper>
          )
        )}

        {meta && (
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ display: "block", marginTop: 8, fontSize: 10 }}
          >
            {meta.provider}/{meta.model} — {meta.originalLength} → {meta.revisedLength} chars
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleApply}
          disabled={loading || !revised}
        >
          Substituir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewTextModal;
